/**
 * Ludo AI
 *
 * Three difficulty levels:
 * - Easy: random valid move, 500ms delay
 * - Medium: priority-based (capture > safe spot > enter > advance closest), 1s delay
 * - Hard: scoring function with blocking and risk assessment, 2s delay
 */

import {
  LudoDifficulty,
  LudoMove,
  PlayerColor,
  TokenPosition,
  SAFE_SPOTS,
  PLAYER_START_POSITIONS,
  HOME_STRETCH_ENTRY,
  TRACK_LENGTH,
  HOME_STRETCH_LENGTH,
} from './types';
import { LudoEngine } from './LudoEngine';

export class LudoAI {
  private difficulty: LudoDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: LudoDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  /** Select the best move for the given difficulty */
  selectMove(
    engine: LudoEngine,
    player: PlayerColor,
    diceValue: number,
    validMoves: LudoMove[]
  ): LudoMove {
    if (validMoves.length === 0) {
      // Should not happen — caller should check first
      return { player, tokenIndex: 0, action: 'skip', diceValue };
    }
    if (validMoves.length === 1) {
      return validMoves[0];
    }

    switch (this.difficulty) {
      case 'easy':
        return this.selectEasy(validMoves);
      case 'medium':
        return this.selectMedium(engine, player, validMoves);
      case 'hard':
        return this.selectHard(engine, player, validMoves);
      default:
        return this.selectEasy(validMoves);
    }
  }

  /** Get the delay in ms before the AI acts */
  getDelay(): number {
    switch (this.difficulty) {
      case 'easy': return 500;
      case 'medium': return 1000;
      case 'hard': return 2000;
      default: return 1000;
    }
  }

  setDifficulty(difficulty: LudoDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): LudoDifficulty {
    return this.difficulty;
  }

  /** Schedule an AI action after the configured delay */
  scheduleAction(onAction: () => void): () => void {
    this.cancelPending();
    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      onAction();
    }, delay);
    return () => this.cancelPending();
  }

  cancelPending(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  reset(): void {
    this.cancelPending();
  }

  destroy(): void {
    this.cancelPending();
  }

  // =========================================================================
  // Easy: Random
  // =========================================================================

  private selectEasy(validMoves: LudoMove[]): LudoMove {
    return validMoves[Math.floor(Math.random() * validMoves.length)];
  }

  // =========================================================================
  // Medium: Priority-based
  // =========================================================================

  private selectMedium(engine: LudoEngine, player: PlayerColor, validMoves: LudoMove[]): LudoMove {
    // Priority 1: Capture
    for (const move of validMoves) {
      if (this.wouldCapture(engine, player, move)) {
        return move;
      }
    }

    // Priority 2: Move to safe spot
    for (const move of validMoves) {
      if (this.wouldLandOnSafeSpot(engine, player, move)) {
        return move;
      }
    }

    // Priority 3: Enter new token (if fewer than 2 on board)
    if (engine.getTokensOnBoard(player) < 2) {
      const enterMoves = validMoves.filter((m) => m.action === 'enter');
      if (enterMoves.length > 0) {
        return enterMoves[0];
      }
    }

    // Priority 4: Advance closest to home
    return this.selectClosestToHome(engine, player, validMoves);
  }

  // =========================================================================
  // Hard: Scoring function
  // =========================================================================

  private selectHard(engine: LudoEngine, player: PlayerColor, validMoves: LudoMove[]): LudoMove {
    let bestScore = -Infinity;
    let bestMoves: LudoMove[] = [];

    for (const move of validMoves) {
      const score = this.scoreMove(engine, player, move);
      if (score > bestScore) {
        bestScore = score;
        bestMoves = [move];
      } else if (score === bestScore) {
        bestMoves.push(move);
      }
    }

    // Break ties randomly
    return bestMoves[Math.floor(Math.random() * bestMoves.length)];
  }

  private scoreMove(engine: LudoEngine, player: PlayerColor, move: LudoMove): number {
    let score = 0;
    const tokens = engine.getTokenPositions(player);
    const token = tokens[move.tokenIndex];

    // Capture bonus (+50)
    if (this.wouldCapture(engine, player, move)) {
      score += 50;
    }

    // Reach home bonus (+100)
    if (this.wouldReachHome(engine, player, move)) {
      score += 100;
    }

    // Home stretch advance (+25)
    if (token.location === 'home_stretch') {
      score += 25;
    }

    // Safe spot bonus (+20)
    if (this.wouldLandOnSafeSpot(engine, player, move)) {
      score += 20;
    }

    // Enter board bonus (+15)
    if (move.action === 'enter') {
      score += 15;
    }

    // Block bonus (+30): positioning near opponent's home stretch entry
    if (this.wouldBlock(engine, player, move)) {
      score += 30;
    }

    // Risk penalty (-20): landing within 6 of an opponent's token
    if (this.hasRisk(engine, player, move)) {
      score -= 20;
    }

    // Spread bonus (+10): move trailing token
    if (this.isTrailingToken(engine, player, move)) {
      score += 10;
    }

    return score;
  }

  // =========================================================================
  // Move Analysis Helpers
  // =========================================================================

  private wouldCapture(engine: LudoEngine, player: PlayerColor, move: LudoMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;
    if (SAFE_SPOTS.includes(destPos)) return false;

    const occupants = engine.getSquareOccupants(destPos);
    return occupants.some((o) => o.player !== player);
  }

  private wouldLandOnSafeSpot(engine: LudoEngine, player: PlayerColor, move: LudoMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;
    return SAFE_SPOTS.includes(destPos);
  }

  private wouldReachHome(engine: LudoEngine, player: PlayerColor, move: LudoMove): boolean {
    const tokens = engine.getTokenPositions(player);
    const token = tokens[move.tokenIndex];
    if (token.location === 'home_stretch') {
      return token.homeStretchPosition! + move.diceValue === HOME_STRETCH_LENGTH;
    }
    // Check if track move would enter home
    if (token.location === 'track') {
      const startPos = PLAYER_START_POSITIONS[player];
      const entryPos = HOME_STRETCH_ENTRY[player];
      const relativePos = (token.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const relativeEntry = (entryPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const newRelativePos = relativePos + move.diceValue;
      if (relativePos <= relativeEntry && newRelativePos > relativeEntry) {
        const stepsInto = newRelativePos - relativeEntry - 1;
        return stepsInto === HOME_STRETCH_LENGTH;
      }
    }
    return false;
  }

  private wouldBlock(engine: LudoEngine, player: PlayerColor, move: LudoMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;

    const state = engine.getGameState();
    for (const color of state.activePlayers) {
      if (color === player) continue;
      const entry = HOME_STRETCH_ENTRY[color];
      // Check if destination is within 6 squares before opponent's home stretch entry
      const dist = (entry - destPos + TRACK_LENGTH) % TRACK_LENGTH;
      if (dist > 0 && dist <= 6) {
        return true;
      }
    }
    return false;
  }

  private hasRisk(engine: LudoEngine, player: PlayerColor, move: LudoMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;
    if (SAFE_SPOTS.includes(destPos)) return false;

    const state = engine.getGameState();
    for (const color of state.activePlayers) {
      if (color === player) continue;
      for (const token of state.tokens[color]) {
        if (token.location === 'track') {
          // Check if opponent is within 6 squares behind our destination
          const dist = (destPos - token.trackPosition! + TRACK_LENGTH) % TRACK_LENGTH;
          if (dist > 0 && dist <= 6) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isTrailingToken(engine: LudoEngine, player: PlayerColor, move: LudoMove): boolean {
    const tokens = engine.getTokenPositions(player);
    const token = tokens[move.tokenIndex];
    if (token.location !== 'track') return false;

    const startPos = PLAYER_START_POSITIONS[player];
    const relPos = (token.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;

    // Check if this is the token furthest from home
    let isTrailing = true;
    for (const t of tokens) {
      if (t === token) continue;
      if (t.location === 'track') {
        const otherRel = (t.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
        if (otherRel < relPos) {
          isTrailing = false;
          break;
        }
      }
    }
    return isTrailing;
  }

  private selectClosestToHome(engine: LudoEngine, player: PlayerColor, validMoves: LudoMove[]): LudoMove {
    const tokens = engine.getTokenPositions(player);
    const startPos = PLAYER_START_POSITIONS[player];
    let bestMove = validMoves[0];
    let bestProgress = -1;

    for (const move of validMoves) {
      const token = tokens[move.tokenIndex];
      let progress = 0;

      if (token.location === 'home_stretch') {
        progress = TRACK_LENGTH + token.homeStretchPosition!;
      } else if (token.location === 'track') {
        progress = (token.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      }

      if (progress > bestProgress) {
        bestProgress = progress;
        bestMove = move;
      }
    }

    return bestMove;
  }

  /** Get the destination track position for a move (null if not landing on track) */
  private getDestinationTrackPosition(engine: LudoEngine, player: PlayerColor, move: LudoMove): number | null {
    if (move.action === 'enter') {
      return PLAYER_START_POSITIONS[player];
    }

    const tokens = engine.getTokenPositions(player);
    const token = tokens[move.tokenIndex];

    if (token.location === 'track') {
      const startPos = PLAYER_START_POSITIONS[player];
      const entryPos = HOME_STRETCH_ENTRY[player];
      const relativePos = (token.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const relativeEntry = (entryPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const newRelativePos = relativePos + move.diceValue;

      if (relativePos <= relativeEntry && newRelativePos > relativeEntry) {
        // Entering home stretch — not on track
        return null;
      }
      return (token.trackPosition! + move.diceValue) % TRACK_LENGTH;
    }

    return null;
  }
}
